import Check from '../Check';
import Report from '../Report';
import { MessageType } from '../Message';
import type { IAttribute } from '../../../stores/model/metamodel/attribute';
import type { IObjectType } from '../../../stores/model/metamodel/object_type';

/**
 * Check if an object attribute's name conflicts with the name of the master of a dependency of the
 * object.
 */
class ObjectAttributeNameConflictCheck extends Check<IAttribute> {
  static description = 'Check object attribute name for conflicts';

  check(): Report {
    this.report = new Report();

    // `IAttribute.owner` is typed loosely (IAnyStateTreeNode) in the metamodel
    // to avoid a recursion cycle; this check only runs for object-type-owned
    // attributes (it reads dependencies/supertypes), so the owner is an
    // `IObjectType` here, the same narrowing `AttributeDuplicateCheck` uses.
    const object = this.element.owner as IObjectType;
    const attribute = this.element;
    const lcAttrName = attribute.name.toLowerCase();

    let dependenciesToCheckForMasterName = object.dependenciesAsDependent;
    for (const supertype of object.indirectSupertypes) {
      dependenciesToCheckForMasterName = dependenciesToCheckForMasterName.concat(
        supertype.dependenciesAsDependent
      );
    }

    for (const dep of dependenciesToCheckForMasterName) {
      const lcMasterRoleName = dep.name.master.toLowerCase();
      const lcMasterName = dep.master.name.toLowerCase();
      if (lcAttrName === lcMasterRoleName) {
        this.report.addMessage(
          MessageType.ERROR,
          `Attribute ${attribute.name} in object ${object.asString()} conflicts with the master role name of dependency ${dep.asString()}`,
          this,
          this.element
        );
      } else if (lcAttrName === lcMasterName) {
        this.report.addMessage(
          MessageType.ERROR,
          `Attribute ${attribute.name} in object ${object.asString()} conflicts with the name of the master of dependency ${dep.asString()}`,
          this,
          this.element
        );
      }
    }

    let dependenciesToCheckForDependentName = object.dependenciesAsMaster;
    for (const supertype of object.indirectSupertypes) {
      dependenciesToCheckForDependentName = dependenciesToCheckForDependentName.concat(
        supertype.dependenciesAsMaster
      );
    }
    for (const dep of dependenciesToCheckForDependentName) {
      const lcDependentRoleName = dep.name.dependent.toLowerCase();
      const lcDependentName = dep.dependent.name.toLowerCase();
      if (lcAttrName === lcDependentRoleName) {
        this.report.addMessage(
          MessageType.ERROR,
          `Attribute ${attribute.name} in object ${object.asString()} conflicts with the dependent role name of dependency ${dep.asString()}`,
          this,
          this.element
        );
      } else if (lcAttrName === lcDependentName) {
        this.report.addMessage(
          MessageType.ERROR,
          `Attribute ${attribute.name} in object ${object.asString()} conflicts with the name of the dependent of dependency ${dep.asString()}`,
          this,
          this.element
        );
      }
    }

    return this.report;
  }
}

export default ObjectAttributeNameConflictCheck;
